// FILE: src/pages/MyPreferences.jsx
// Resident-facing preferences page. These fields are resident-owned:
// nursing home staff can read them but can never write them.
import React, { useEffect, useState, useCallback } from "react";
import { getAuth } from "firebase/auth";

const FN_BASE = "https://us-central1-lastwishbox-business.cloudfunctions.net";

const B = {
  red: "#980000",
  redDark: "#650202",
  orange: "#f15822",
  navy: "#003353",
  steel: "#657899",
  bg: "#f6f7fb",
  white: "#fff",
  border: "#e8ebf3",
};

const FIELDS = [
  { key: "preferredName", label: "Preferred name", hint: "What you like to be called day to day.", long: false },
  { key: "nativeLanguage", label: "Native language", hint: "", long: false },
  { key: "religion", label: "Religion or faith", hint: "Only you can enter this. Leave blank if you would rather not say.", long: false },
  { key: "culturalBackground", label: "Cultural background", hint: "Only you can enter this. Leave blank if you would rather not say.", long: false },
  { key: "meaningfulMusic", label: "Music that matters to you", hint: "Songs, hymns or artists you would want played.", long: true },
  { key: "meaningfulObjects", label: "Objects that matter to you", hint: "Photographs, jewellery, books, anything you would want kept close.", long: true },
  { key: "importantLifeEvents", label: "Important life events", hint: "Anything you would want the people caring for you to know about your life.", long: true },
];

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${B.border}`,
  fontSize: 15,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

export default function MyPreferences() {
  const [home, setHome] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error("Not signed in");
      const token = await user.getIdToken();

      const hr = await fetch(`${FN_BASE}/nhMyHome`, { headers: { Authorization: `Bearer ${token}` } });
      const hdata = await hr.json().catch(() => ({}));
      if (!hr.ok) throw new Error(hdata.error || `Request failed (${hr.status})`);

      if (!hdata.linked) {
        setHome(null);
        setLoading(false);
        return;
      }
      setHome(hdata);

      const pr = await fetch(
        `${FN_BASE}/nhGetProfile?tenantId=${encodeURIComponent(hdata.tenantId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const pdata = await pr.json().catch(() => ({}));
      if (!pr.ok) throw new Error(pdata.error || `Request failed (${pr.status})`);

      const p = pdata.profile || {};
      const f = {};
      FIELDS.forEach(({ key }) => { f[key] = p[key] == null ? "" : p[key]; });
      setForm(f);
    } catch (e) {
      console.error("MyPreferences load failed", e);
      setErr(String(e.message || e));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!home?.tenantId) return;
    setSaving(true);
    setErr("");
    setOk("");
    try {
      const user = getAuth().currentUser;
      const token = await user.getIdToken();

      const fields = {};
      FIELDS.forEach(({ key }) => { fields[key] = form[key] == null ? "" : form[key]; });

      const r = await fetch(`${FN_BASE}/nhSaveProfile`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ tenantId: home.tenantId, fields }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);

      const n = (data.changed || []).length;
      setOk(n ? "Your preferences have been saved." : "No changes to save.");
    } catch (e) {
      console.error("MyPreferences save failed", e);
      setErr(String(e.message || e));
    }
    setSaving(false);
  };

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  if (loading) {
    return <div style={{ padding: 24, color: B.steel }}>Loading&hellip;</div>;
  }

  if (!home) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 16px" }}>
        <h1 style={{ color: B.redDark, fontSize: 28, marginBottom: 8 }}>About me</h1>
        <div style={{ background: B.bg, border: `1px solid ${B.border}`, borderRadius: 12, padding: 20, color: B.steel, fontSize: 14 }}>
          This page is for residents of a care home using LastWishBox. Your account is not linked to one.
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 16px" }}>
      <h1 style={{ color: B.redDark, fontSize: 28, marginBottom: 6 }}>About me</h1>
      <p style={{ color: B.steel, fontSize: 15, marginTop: 0, marginBottom: 20 }}>
        Things you would like the people caring for you to know.
      </p>

      <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af", borderRadius: 12, padding: "14px 18px", fontSize: 14, fontWeight: 600, marginBottom: 24 }}>
        Shared with {home.tenantName || "your care home"}. Only you can fill these in or change them.
        Staff can read them but cannot edit them. Leave anything blank if you would rather not answer.
      </div>

      {err && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, padding: "12px 16px", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{err}</div>
      )}
      {ok && (
        <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", borderRadius: 10, padding: "12px 16px", fontSize: 14, fontWeight: 600, marginBottom: 16 }}>{ok}</div>
      )}

      <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 16, padding: "22px 24px" }}>
        {FIELDS.map(({ key, label, hint, long }) => (
          <div key={key} style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: 14, fontWeight: 700, color: B.redDark, marginBottom: 6 }}>{label}</label>
            {long ? (
              <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={form[key] || ""} onChange={(e) => setF(key, e.target.value)} />
            ) : (
              <input style={inputStyle} value={form[key] || ""} onChange={(e) => setF(key, e.target.value)} />
            )}
            {hint && <div style={{ fontSize: 12, color: B.steel, marginTop: 5 }}>{hint}</div>}
          </div>
        ))}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
          <button
            onClick={save}
            disabled={saving}
            style={{
              padding: "11px 26px", borderRadius: 10, border: "none",
              background: B.orange, color: B.white, fontWeight: 700, fontSize: 15,
              cursor: saving ? "default" : "pointer", opacity: saving ? .7 : 1,
            }}
          >{saving ? "Saving\u2026" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
